import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useRef } from 'react';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useScrollTransition } from 'util/react-transition-component';
import { renderCta } from 'util/cta/cta.template';
import type { CtaRefType } from 'util/cta/cta.data';
import type { PrimaryLinkProps } from './PrimaryLink.data';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './PrimaryLink.transitions';
import styles from './PrimaryLink.module.scss';
import Icon from '../icon/Icon';

export default function PrimaryLink({
  isVisible,
  transitionInType = 'none',
  transitionOutType = 'none',
  disableTriggerInOnScroll = true,
  scrollTransitionVars,
  canTransitionIn,
  textVariant,
  className,
  isActive,
  icon,
  ref,
  ...props
}: PrimaryLinkProps): ReactElement {
  const localRef = useRef<CtaRefType>(undefined);
  const elementRef = (ref || localRef) as RefObject<CtaRefType | null>;
  const iconRef = useRef<HTMLElement>(null);

  const { transitionController } = useScrollTransition({
    scroll: {
      canTransitionIn,
      disableTriggerInOnScroll,
      trigger: elementRef,
      vars: scrollTransitionVars,
    },
    setupOptions: () => ({
      ref: elementRef,
      refs: {
        elementRef,
        iconRef,
        transitionInType,
        transitionOutType,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
  });

  useChangeTransitionState(transitionController, isVisible);

  return renderCta({
    innerTemplate: (
      <>
        {props.label}
        {icon && (
          <Icon ref={iconRef} name={icon} className={classNames(styles.icon, styles[icon])} />
        )}
      </>
    ),
    props: {
      ...props,
      className: classNames(styles.primaryLink, styles[textVariant || ''], className, {
        [styles.isActive]: isActive,
      }),
    },
    elementRef,
  });
}
